"use client";

import React from "react";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate,useParams,useLocation } from "react-router-dom";
import DataTable, { createTheme } from "react-data-table-component";
import DaySelector from "../AdminDias";
import TrHeader from "../transporte/common/components/TrHeader";
import TrFiltros from "../transporte/common/components/TrFiltros";
import TrHeaderMenuPicker from "../transporte/common/components/TrHeaderMenuPicker";
import { customStylesEncomienda } from "../transporte/common/components/TrOperacionRow";

import { Box, Typography, InputBase, MenuItem,Select, useMediaQuery } from "@mui/material";
import { Search, FileText, Calendar, ClipboardList, Pencil, Trash2, UserPen, Clock3, CheckCircle2, Filter, FileSpreadsheet, CopyPlus } from "lucide-react";
import { Plus } from 'lucide-react';

import Tooltip from '@mui/material/Tooltip';
import AppSearch from "../../ui/AppSearch";
import AppButton from "../../ui/AppButton";
import AppChip from "../../ui/AppChip";
import palette from "../../../theme/palette";
import createPresupuestoPdf from "./AdminVentaPresupuestoPdf";
import TrabajoInfoModal from "./modals/TrabajoInfoModal";
import ClonarTrabajoModal from "./modals/ClonarTrabajoModal";
import SunatIcon from "../../../assets/images/sunat0.png";

/* =======================================================
   PALETA (manteniendo tu dark + teal)
======================================================= */
import axios from 'axios';
import * as XLSX from "xlsx";
import swal from 'sweetalert';
import swal2 from 'sweetalert2'
import AdminSunatIcon from '../AdminSunatIcon';
import AdminSunatGreIcon from '../AdminSunatGreIcon';
import DeleteIcon from '@mui/icons-material/Delete';
import FindIcon from '@mui/icons-material/FindInPage';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { blueGrey } from '@mui/material/colors';
import { useAuth0 } from '@auth0/auth0-react'; //new para cargar permisos luego de verificar registro en bd
import { useDialog } from "../AdminConfirmDialogProvider";
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
/*const palette = {
  bg: "#1a2127",
  surface: "#1e272e",
  surfaceAlt: "#222d35",
  chip: "#273238",
  border: "#2d3942",
  borderSoft: "#26323a",
  text: "#ffffff",
  muted: "#8b9aa5",
  accent: "#2aa198",
  accentSoft: "rgba(42,161,152,0.12)",
};*/

/* =======================================================
   TEMA DATATABLE
======================================================= */

createTheme(
  "solarized",
  {
    text: { primary: palette.text, secondary: palette.accent },
    background: { default: "transparent" },
    context: { background: palette.accent, text: "#FFFFFF" },
    divider: { default: palette.borderSoft },
    action: {
      button: "rgba(255,255,255,.54)",
      hover: "rgba(42,161,152,0.06)",
      disabled: "rgba(255,255,255,.12)",
    },
  },
  "dark",
);

/* =======================================================
   DATOS DEMO
======================================================= */

const formatMoney = (moneda, value) => `${moneda || "PEN"} ${Number(value || 0).toLocaleString("es-PE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const numeroPresupuesto = (presupuesto) => [
  presupuesto.r_cod,
  presupuesto.r_serie,
  presupuesto.r_numero,
].filter(Boolean).join("-");

const formatFecha = (fecha) => {
  const fechaTexto = String(fecha || "").slice(0, 10);
  if (!fechaTexto) {
    return "";
  }
  return fechaTexto.split("-").reverse().join("/");
};

const normalizarPresupuestoListado = (presupuesto) => ({
  ...presupuesto,
  numero: numeroPresupuesto(presupuesto),
  fecha: formatFecha(presupuesto.r_fecemi),
  cliente: presupuesto.r_razon_social || "Sin cliente",
  autor: presupuesto.ctrl_crea_us || presupuesto.id_invitado || "Sin autor",
  moneda: presupuesto.r_moneda || "PEN",
  total: Number(presupuesto.r_monto_total || 0),
  servicios_count: Number(presupuesto.servicios_count || presupuesto.servicios?.length || presupuesto.trabajos?.length || 0),
  trabajos: (presupuesto.servicios || presupuesto.trabajos || []).map((trabajo) => ({
    ...trabajo,
    id: trabajo.servicio || trabajo.id,
    numero: trabajo.descripcion || `Servicio ${trabajo.servicio || ""}`,
    materiales: trabajo.materiales || trabajo.detalles || [],
  })),
});

const resumirMontosPresupuestos = (presupuestos) => {
  const totales = new Map();
  presupuestos.forEach((presupuesto) => {
    const monedaRaw = String(presupuesto.r_moneda || presupuesto.moneda || "PEN").trim().toUpperCase();
    const moneda = ["PEN", "SOL", "S/", "S"].includes(monedaRaw)
      ? "PEN"
      : ["USD", "US$", "$", "DOLAR", "DÓLAR", "DOLARES", "DÓLARES"].includes(monedaRaw)
        ? "USD"
        : monedaRaw;
    totales.set(moneda, (totales.get(moneda) || 0) + Number(presupuesto.r_monto_total || presupuesto.total || 0));
  });

  if (!totales.size) {
    return "S/ 0.00";
  }

  return [...totales.entries()].map(([moneda, total]) => {
    const prefijo = moneda === "PEN" ? "S/" : moneda === "USD" ? "US$" : `${moneda} `;
    const monto = new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(total);
    return `${prefijo} ${monto}`;
  }).join(" · ");
};

const normalizarPresupuestoFull = (presupuesto) => ({
  ...normalizarPresupuestoListado(presupuesto),
  trabajos: (presupuesto.servicios || []).map((servicio) => ({
    ...servicio,
    id: servicio.servicio,
    numero: servicio.descripcion || `Servicio ${servicio.servicio || ""}`,
    materiales: servicio.detalles || [],
  })),
});

const tieneComprobanteGenerado = (presupuesto) => Boolean(
  presupuesto.fact_cod && presupuesto.fact_serie && presupuesto.fact_num
);

const comprobanteGeneradoPresupuesto = (presupuesto) => {
  if (!tieneComprobanteGenerado(presupuesto)) {
    return "";
  }

  return presupuesto.fact_comprobante || [
    presupuesto.fact_cod,
    presupuesto.fact_serie,
    presupuesto.fact_num,
  ].filter(Boolean).join("-");
};

const comprobanteGeneradoKeyPresupuesto = (presupuesto) => {
  const comprobante = comprobanteGeneradoPresupuesto(presupuesto);
  const elemento = presupuesto.fact_elemento || 1;
  return presupuesto.fact_comprobante_key || `${comprobante}-${elemento}`;
};

const renderGenerarComprobanteIcon = () => (
  <Box
    component="img"
    src={SunatIcon}
    alt="SUNAT"
    sx={{
      width: { xs: 26, sm: 26 },
      height: { xs: 26, sm: 26 },
      objectFit: "contain",
      filter: "grayscale(0.65)",
    }}
  />
);

const sunatPendienteEnvioFilter = "brightness(0) saturate(100%) invert(48%) sepia(42%) saturate(700%) hue-rotate(129deg) brightness(95%) contrast(92%)";

const tieneComprobanteEnSunat = (presupuesto) => Boolean(
  presupuesto.fact_r_vfirmado || presupuesto.fact_cdr_nivel === "ACEPTADO"
);

function PresupuestoFiltroCard({ label, value, icon: Icon, color, soft, active, onClick, title }) {
  const interactiva = typeof onClick === "function";
  const colorSoft = soft || `${color}20`;
  return (
    <Box
      role={interactiva ? "button" : undefined}
      tabIndex={interactiva ? 0 : undefined}
      aria-pressed={active}
      title={title}
      onClick={onClick}
      onKeyDown={interactiva ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      } : undefined}
      sx={{
        minWidth: 0,
        position: "relative",
        p: { xs: 0.95, sm: 1.05 },
        py: 0.75,
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        alignItems: "stretch",
        gap: 0.7,
        border: `1px solid ${active ? color : palette.borderSoft}`,
        borderRadius: palette.radius.listCard,
        backgroundColor: palette.surface,
        boxShadow: active ? `0 0 0 1px ${color}` : "none",
        color,
        cursor: interactiva ? "pointer" : "default",
        transition: "border-color .16s ease, background-color .16s ease, box-shadow .16s ease",
        ...(interactiva ? {
          "&:hover": { borderColor: color, backgroundColor: colorSoft },
          "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: 2 },
        } : {}),
      }}
    >
      <Box sx={{ width: 28, height: 28, borderRadius: palette.radius.control, display: "grid", placeItems: "center", backgroundColor: colorSoft, color, alignSelf: "center" }}>
        <Icon size={15} />
      </Box>
      <Box sx={{ minWidth: 0, alignSelf: "center" }}>
        <Typography sx={{ color: palette.muted, fontSize: "9.5px", fontWeight: 900, textTransform: "uppercase", letterSpacing: 0 }} noWrap>{label}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.45, minWidth: 0 }}>
          <Typography sx={{ color: palette.text, fontSize: "16px", lineHeight: 1.05, fontWeight: "1000 !important", WebkitTextStroke: "0.16px currentColor" }} noWrap>{value}</Typography>
          {active && (
            <Box sx={{ width: 16, height: 16, borderRadius: "999px", backgroundColor: color, color: palette.onAccent, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <Filter size={9} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function IndicadorFiltroSeleccionado({ active, color }) {
  if (!active) return null;
  return (
    <Box sx={{ width: 16, height: 16, borderRadius: "999px", backgroundColor: color, color: palette.onAccent, display: "grid", placeItems: "center", flexShrink: 0 }}>
      <Filter size={9} />
    </Box>
  );
}

function PresupuestoEstadoFiltroCard({ pendientes, cerrados, estadoFiltro, onFilter }) {
  const pendienteActivo = estadoFiltro === "P";
  const cerradoActivo = estadoFiltro === "C";
  const colorActivo = pendienteActivo ? palette.warning : cerradoActivo ? palette.success : palette.borderSoft;
  const manejarTeclado = (event, estado) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onFilter(estado);
  };

  return (
    <Box sx={{ minWidth: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", border: `1px solid ${colorActivo}`, borderRadius: palette.radius.listCard, backgroundColor: palette.surface, boxShadow: pendienteActivo ? `0 0 0 1px ${palette.warning}` : cerradoActivo ? `0 0 0 1px ${palette.success}` : "none", overflow: "hidden", transition: "border-color .16s ease, box-shadow .16s ease" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={pendienteActivo}
        onClick={() => onFilter("P")}
        onKeyDown={(event) => manejarTeclado(event, "P")}
        sx={{ minWidth: 0, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", alignItems: "center", gap: 0.65, px: { xs: 0.85, sm: 1 }, py: 0.75, borderRight: `1px solid ${palette.borderSoft}`, backgroundColor: pendienteActivo ? palette.warningSoft : "transparent", color: palette.warning, cursor: "pointer", borderTopLeftRadius: palette.radius.listCard, borderBottomLeftRadius: palette.radius.listCard, transition: "background-color .16s ease", "&:hover": { backgroundColor: palette.warningSoft }, "&:focus-visible": { outline: `2px solid ${palette.warning}`, outlineOffset: -2 } }}
      >
        <Box sx={{ width: 28, height: 28, borderRadius: palette.radius.control, display: "grid", placeItems: "center", backgroundColor: palette.warningSoft, color: palette.warning }}>
          <Clock3 size={15} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: palette.muted, fontSize: "9.5px", fontWeight: 900, textTransform: "uppercase", lineHeight: 1.15 }} noWrap>Pendientes</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.45, minWidth: 0 }}>
            <Typography sx={{ color: palette.text, fontSize: "16px", lineHeight: 1.05, fontWeight: "1000 !important", WebkitTextStroke: "0.16px currentColor" }} noWrap>{pendientes}</Typography>
            <IndicadorFiltroSeleccionado active={pendienteActivo} color={palette.warning} />
          </Box>
        </Box>
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={cerradoActivo}
        onClick={() => onFilter("C")}
        onKeyDown={(event) => manejarTeclado(event, "C")}
        sx={{ minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0.25, px: { xs: 0.85, sm: 1 }, py: 0.75, backgroundColor: palette.successSoft, color: palette.success, cursor: "pointer", borderTopRightRadius: palette.radius.listCard, borderBottomRightRadius: palette.radius.listCard, transition: "background-color .16s ease", "&:hover": { backgroundColor: palette.successSoft }, "&:focus-visible": { outline: `2px solid ${palette.success}`, outlineOffset: -2 } }}
      >
        <Typography sx={{ color: palette.muted, fontSize: "9.5px", fontWeight: 900, lineHeight: 1.15, textTransform: "uppercase" }} noWrap>Cerrados</Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.45, minWidth: 0 }}>
          <Typography sx={{ color: palette.text, fontSize: "16px", lineHeight: 1.05, fontWeight: "1000 !important", WebkitTextStroke: "0.16px currentColor" }} noWrap>{cerrados}</Typography>
          <IndicadorFiltroSeleccionado active={cerradoActivo} color={palette.success} />
        </Box>
      </Box>
    </Box>
  );
}

function PresupuestoCpeFiltroCard({ generados, sinCpe, cpeFiltro, onFilter }) {
  const generadoActivo = cpeFiltro === "con";
  const sinCpeActivo = cpeFiltro === "sin";
  const colorActivo = generadoActivo ? "#0891b2" : sinCpeActivo ? palette.danger : palette.borderSoft;

  return (
    <Box sx={{ minWidth: 0, display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", border: `1px solid ${colorActivo}`, borderRadius: palette.radius.listCard, backgroundColor: palette.surface, boxShadow: generadoActivo ? "0 0 0 1px #0891b2" : sinCpeActivo ? `0 0 0 1px ${palette.danger}` : "none", overflow: "hidden", transition: "border-color .16s ease, box-shadow .16s ease" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={generadoActivo}
        onClick={() => onFilter("con")}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onFilter("con");
        }}
        sx={{ minWidth: 0, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", alignItems: "center", gap: 0.65, px: { xs: 0.85, sm: 1 }, py: 0.75, borderRight: `1px solid ${palette.borderSoft}`, backgroundColor: generadoActivo ? "rgba(8,145,178,0.14)" : "transparent", color: "#0891b2", cursor: "pointer", borderTopLeftRadius: palette.radius.listCard, borderBottomLeftRadius: palette.radius.listCard, transition: "background-color .16s ease", "&:hover": { backgroundColor: "rgba(8,145,178,0.14)" }, "&:focus-visible": { outline: "2px solid #0891b2", outlineOffset: -2 } }}
      >
        <Box sx={{ width: 28, height: 28, borderRadius: palette.radius.control, display: "grid", placeItems: "center", backgroundColor: "rgba(8,145,178,0.14)", color: "#0891b2" }}>
          <FileText size={15} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: palette.muted, fontSize: "9.5px", fontWeight: 900, textTransform: "uppercase", lineHeight: 1.15 }} noWrap>CPE generado</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.45, minWidth: 0 }}>
            <Typography sx={{ color: palette.text, fontSize: "16px", lineHeight: 1.05, fontWeight: "1000 !important", WebkitTextStroke: "0.16px currentColor" }} noWrap>{generados}</Typography>
            <IndicadorFiltroSeleccionado active={generadoActivo} color="#0891b2" />
          </Box>
        </Box>
      </Box>
      <Box
        role="button"
        tabIndex={0}
        aria-pressed={sinCpeActivo}
        onClick={() => onFilter("sin")}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onFilter("sin");
        }}
        sx={{ minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 0.25, px: { xs: 0.85, sm: 1 }, py: 0.75, backgroundColor: palette.dangerSoft, color: palette.danger, cursor: "pointer", borderTopRightRadius: palette.radius.listCard, borderBottomRightRadius: palette.radius.listCard, transition: "background-color .16s ease", "&:hover": { backgroundColor: palette.dangerSoft }, "&:focus-visible": { outline: `2px solid ${palette.danger}`, outlineOffset: -2 } }}
      >
        <Typography sx={{ color: palette.muted, fontSize: "9.5px", fontWeight: 900, lineHeight: 1.15, textTransform: "uppercase" }} noWrap>Sin CPE</Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.45, minWidth: 0 }}>
          <Typography sx={{ color: palette.text, fontSize: "16px", lineHeight: 1.05, fontWeight: "1000 !important", WebkitTextStroke: "0.16px currentColor" }} noWrap>{sinCpe}</Typography>
          <IndicadorFiltroSeleccionado active={sinCpeActivo} color={palette.danger} />
        </Box>
      </Box>
    </Box>
  );
}

/* =======================================================
   COLUMNAS
======================================================= */

const createColumns = (onEdit, onView, onPdf, onTrabajoInfo, onDelete, onGenerarComprobante, renderSunatComprobante, onOpenComprobanteGenerado, onClonePresupuesto) => [
  {
    name: "",
    grow: 1,
    minWidth: "0px",
    maxWidth: "100%",
    cell: (row) => {
      const comprobanteGenerado = comprobanteGeneradoPresupuesto(row);
      const presupuestoPendiente = (row.estado || "P") === "P";

      return (
      <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", py: 0.4 }}>
        {/* FILA SUPERIOR */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flexWrap: "wrap" }}>

            <Tooltip title="PDF cliente" arrow>
              <Box
                onClick={() => onPdf(row)}
                sx={{
                  width: { xs: 40, sm: 30 },
                  height: { xs: 40, sm: 30 },
                  borderRadius: { xs: 2, sm: 1.5 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: palette.accentSoft,
                  color: palette.accent,
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "all .18s ease",
                  "&:hover": { backgroundColor: palette.accent, color: "#ffffff" },
                  "& svg": {
                    width: { xs: 20, sm: 16 },
                    height: { xs: 20, sm: 16 },
                  },
                }}
              >
                <FileText size={16} />
              </Box>
            </Tooltip>

            {comprobanteGenerado && (
              <Tooltip title="Abrir comprobante generado" arrow>
                <Typography
                  component="span"
                  onClick={() => onOpenComprobanteGenerado(row)}
                  sx={{
                    color: palette.text,
                    fontWeight: 700,
                    fontSize: "15px",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {comprobanteGenerado}
                </Typography>
              </Tooltip>
            )}

            <Typography
              sx={{
                color: palette.text,
                fontWeight: 700,
                fontSize: "15px",
                letterSpacing: "0.3px",
                whiteSpace: "nowrap",
              }}
            >
              Proforma: {row.numero}
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 0.85,
                py: 0.4,
                borderRadius: 1.25,
                border: `1px solid ${presupuestoPendiente ? palette.warning : palette.border}`,
                borderLeft: presupuestoPendiente ? `3px solid ${palette.warning}` : undefined,
                color: presupuestoPendiente ? palette.warning : palette.muted,
                fontSize: "10.5px",
                fontWeight: presupuestoPendiente ? 700 : 500,
                textTransform: "uppercase",
                letterSpacing: "0.35px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {presupuestoPendiente ? "Pendiente" : "Cerrado"}
            </Box>

          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-start" },
              flexWrap: "wrap",
              gap: { xs: 0.85, sm: 1 },
              width: { xs: "100%", sm: "auto" },
              mt: { xs: 0.75, sm: 0 },
            }}
          >
            {tieneComprobanteGenerado(row) ? (
              <Tooltip title={tieneComprobanteEnSunat(row) ? "Ver CPE en SUNAT" : "Enviar CPE a SUNAT"} arrow>
                <Box
                  sx={{
                    width: { xs: 42, sm: 30 },
                    height: { xs: 42, sm: 30 },
                    borderRadius: { xs: 2, sm: 1.5 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: palette.chip,
                    border: `1px solid ${palette.border}`,
                    boxShadow: { xs: "0 8px 18px rgba(0,0,0,.16)", sm: "none" },
                    "& img": {
                      filter: tieneComprobanteEnSunat(row)
                        ? "grayscale(0) !important"
                        : `${sunatPendienteEnvioFilter} !important`,
                    },
                    "&:hover": {
                      borderColor: tieneComprobanteEnSunat(row) ? "#2563eb" : palette.accent,
                      backgroundColor: tieneComprobanteEnSunat(row)
                        ? "rgba(37,99,235,0.14)"
                        : "rgba(42,161,152,0.16)",
                    },
                  }}
                >
                  {renderSunatComprobante(row)}
                </Box>
              </Tooltip>
            ) : (
              <Tooltip title="Generar comprobante" arrow>
                <Box
                  onClick={() => onGenerarComprobante(row)}
                  sx={{
                    width: { xs: 42, sm: 30 },
                    height: { xs: 42, sm: 30 },
                    borderRadius: { xs: 2, sm: 1.5 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: palette.chip,
                    border: `1px solid ${palette.border}`,
                    color: palette.muted,
                    cursor: "pointer",
                    transition: "all .18s ease",
                    boxShadow: { xs: "0 8px 18px rgba(0,0,0,.16)", sm: "none" },
                    "&:hover": {
                      backgroundColor: palette.accent,
                      borderColor: palette.accent,
                      color: "#ffffff",
                    },
                    "&:hover img": {
                      filter: "grayscale(0)",
                    },
                  }}
                >
                  {renderGenerarComprobanteIcon()}
                </Box>
              </Tooltip>
            )}

            {[
              { title: "Editar presupuesto", icon: <Pencil size={14} />, onClick: () => onEdit(row) },
              { title: "Clonar presupuesto", icon: <CopyPlus size={14} />, onClick: () => onClonePresupuesto(row) },
              {
                title: (row.estado || "P") === "P" ? "Eliminar presupuesto" : "Solo pendientes",
                icon: <Trash2 size={14} />,
                onClick: () => onDelete(row),
                danger: true,
                disabled: (row.estado || "P") !== "P",
              },
            ].map((action) => (
              <Tooltip key={action.title} title={action.title} arrow>
                <Box
                  onClick={action.disabled ? undefined : action.onClick}
                  sx={{
                    width: { xs: 42, sm: 30 },
                    height: { xs: 42, sm: 30 },
                    borderRadius: { xs: 2, sm: 1.5 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: palette.chip,
                    border: `1px solid ${palette.border}`,
                    color: action.disabled ? palette.border : palette.muted,
                    cursor: action.disabled ? "not-allowed" : "pointer",
                    opacity: action.disabled ? 0.55 : 1,
                    transition: "all .18s ease",
                    boxShadow: { xs: "0 8px 18px rgba(0,0,0,.16)", sm: "none" },
                    "& svg": {
                      width: { xs: 20, sm: 14 },
                      height: { xs: 20, sm: 14 },
                    },
                    "&:hover": action.disabled ? {} : {
                      backgroundColor: action.danger ? "#c2410c" : palette.accent,
                      borderColor: action.danger ? "#c2410c" : palette.accent,
                      color: "#ffffff",
                    },
                  }}
                >
                  {action.icon}
                </Box>
              </Tooltip>
            ))}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: palette.accent,
                fontWeight: 600,
                fontSize: "12.5px",
                ml: { xs: 0.25, sm: 0 },
                height: { xs: 42, sm: "auto" },
              }}
            >
              <Calendar size={13} />
              {row.fecha}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
          <Typography sx={{ color: palette.muted, fontSize: "13px", minWidth: 0 }} noWrap>
            {row.cliente}
          </Typography>
          <Typography sx={{ color: palette.text, fontSize: { xs: "14px", sm: "16px" }, lineHeight: 1.05, fontWeight: "1000 !important", WebkitTextStroke: { xs: "0.2px currentColor", sm: "0.32px currentColor" }, whiteSpace: "nowrap", flexShrink: 0 }}>
            {formatMoney(row.moneda, row.total)}
          </Typography>
        </Box>

        {/* TRABAJOS / USUARIO QUE GRABO */}
        <Box
          sx={{
            mt: 0.5,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, minWidth: 0, width: { xs: "100%", sm: "auto" }, maxWidth: "100%", flex: "1 1 260px", boxSizing: "border-box" }}>
            {row.trabajos.length > 0 ? row.trabajos.map((trabajo, index) => {
              const descripcion = trabajo.descripcion || trabajo.numero || `Servicio ${trabajo.servicio || ""}`;
              const descripcionCorta = descripcion.length > 15 ? `${descripcion.slice(0, 15)}...` : descripcion;
              const codigoTrabajo = `T${String(index + 1).padStart(2, "0")}: ${descripcionCorta}`;

              return (
                <Tooltip key={trabajo.id} title={descripcion} arrow>
                  <AppChip
                    onClick={() => onTrabajoInfo(row, trabajo)}
                    sx={{ width: { xs: "100%", sm: "28ch" }, maxWidth: "100%", px: { xs: 1.5, sm: 0.75 }, boxSizing: "border-box", flexShrink: { xs: 1, sm: 0 }, justifyContent: "flex-start", overflow: "hidden", whiteSpace: "nowrap" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0, width: "100%", overflow: "hidden" }}>
                      <ClipboardList size={13} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{codigoTrabajo}</span>
                    </Box>
                  </AppChip>
                </Tooltip>
              );
            }) : row.servicios_count > 0 && (
              <Typography sx={{ color: palette.muted, fontSize: "12px" }}>
                Sin detalle de trabajos disponible
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              ml: { xs: 0, sm: "auto" },
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: 0,
              width: { xs: "100%", sm: "auto" },
              maxWidth: { xs: "100%", sm: "36%" },
              px: 0,
              py: 0.25,
              color: palette.muted,
            }}
          >
            <UserPen size={14} style={{ flexShrink: 0 }} />
            <Typography sx={{ color: palette.muted, fontSize: "12.5px", minWidth: 0 }} noWrap>
              {row.autor}
            </Typography>
          </Box>
        </Box>
      </Box>
      );
    },
  },
];

/* =======================================================
   ESTILOS DATATABLE
======================================================= */

const customStyles = {
  ...customStylesEncomienda,
  table: {
    ...customStylesEncomienda.table,
    style: { ...customStylesEncomienda.table.style, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" },
  },
  rows: {
    ...customStylesEncomienda.rows,
    style: {
      ...customStylesEncomienda.rows.style,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      "@media (max-width: 600px)": {
        width: "90%",
        maxWidth: "90%",
        marginLeft: 0,
        marginRight: "auto",
      },
    },
  },
};


/* =======================================================
   COMPONENTE
======================================================= */

export default function AdminVentaPresupuestoList({ idAnfitrion, idInvitado, super: superUsuarioProp = "0" }) {
  const back_host = process.env.BACK_HOST || "https://xpertcont-backend-js-production-50e6.up.railway.app";
  //experimento
  const [updateTrigger, setUpdateTrigger] = useState({});

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
	//const [data, setData] = useState(tableDataItems);
  const [registrosdet,setRegistrosdet] = useState([]);
  const cargaRegistroSeq = useRef(0);
  const [tabladet,setTabladet] = useState([]);  //Copia de los registros: Para tratamiento de filtrado
  const [estadoFiltro, setEstadoFiltro] = useState("*");
  const [cpeFiltro, setCpeFiltro] = useState("*");
  const [paginaReset, setPaginaReset] = useState(false);
  const [navegadorMovil, setNavegadorMovil] = useState(false);
  const [valorBusqueda, setValorBusqueda] = useState(""); //txt: rico filtrado
  const [valorVista, setValorVista] = useState("ventas");
  const [id_libro, setValorLibro] = useState("014");
  const [permisosComando, setPermisosComando] = useState([]); //MenuComandos
  const {user, isAuthenticated } = useAuth0();
  
  const [columnas, setColumnas] = useState([]);

  const [periodo_trabajo, setPeriodoTrabajo] = useState("");
  const [periodo_select,setPeriodosSelect] = useState([]);
  
  const [contabilidad_trabajo, setContabilidadTrabajo] = useState("");
  const [contabilidad_nombre, setContabilidadNombre] = useState("");
  const [contabilidad_select,setContabilidadesSelect] = useState([]);
  const [valorComprobante, setValorComprobante] = useState("");
  const [trabajoInfo, setTrabajoInfo] = useState(null);
  const [clonarTrabajoInfo, setClonarTrabajoInfo] = useState(null);
  const [clonandoTrabajo, setClonandoTrabajo] = useState(false);

  const [datosPopUp,setDatosPopUp] = useState([]);
  let [diaSel, setDiaSel] = useState("*");

  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const { confirmDialog } = useDialog(); //unico dialogo
  const [fecha_clon, setFechaClon] = useState("");

  //Seccion Dialog
  const [isDialogOpen, setDialogOpen] = useState(false);
  const location = useLocation();


    // Agrega íconos al inicio de cada columna
    let columnasComunes;
    //Permisos Nivel 01 - Menus (toggleButton)
    const [permisos, setPermisos] = useState([]); //Menu
    const [permisoVentas, setPermisoVentas] = useState(false);
    
    //Permisos Nivel 02 - Comandos (Buttons)
    const [pVenta0101, setPVenta0101] = useState(false); //Nuevo (Casi libre)
    const [pVenta0102, setPVenta0102] = useState(false); //Modificar (Restringido)
    const [pVenta0103, setPVenta0103] = useState(false); //ELiminar (Restringido)
    const [pVenta0104, setPVenta0104] = useState(false); //Eliminar Masivo (Casi Nunca solo el administrador)
  
    // valores adicionales para Carga Archivo
    const [datosCarga, setDatosCarga] = useState({
      id_anfitrion: '',
      documento_id: '',
      periodo: '',
      id_libro: '',
      id_invitado: '',
    });  
  
    const handleRowSelected = useCallback(state => {
      setSelectedRows(state.selectedRows);
    }, []);
  
    const handleUpdate = (sComprobante,bModoVista) => {
      console.log('sComprobante: ',sComprobante);
      //var num_asiento;
      if (bModoVista) {
        //Validamos
        navigate(`/ad_venta/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo}/${contabilidad_trabajo}/${sComprobante}/view`);
      } else {
        navigate(`/ad_venta/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo}/${contabilidad_trabajo}/${sComprobante}/-`);
      }    
    };
    const handleDelete = (comprobante,elemento) => {
      //Recuerda que el comprobante enviado es el comprobante_key --> contiene el key del registro ;)
      confirmaEliminacion(params.id_anfitrion,contabilidad_trabajo,periodo_trabajo,comprobante,elemento);
    };
    const confirmaEliminacion = async(sAnfitrion,sDocumentoId,sPeriodo,sComprobante,sElemento)=>{
      const result = await confirmDialog({
          title: "Eliminar Comprobante?",
          message: `${sComprobante}`,
          icon: "warning", // success | error | info | warning
          confirmText: "ELIMINAR",
          cancelText: "CANCELAR",
      });
      if (result.isConfirmed) {
            console.log("✅ Eliminado:", sComprobante);
            eliminarRegistroSeleccionado(sAnfitrion,sDocumentoId,sPeriodo,sComprobante,sElemento);
            setToggleCleared(!toggleCleared);
            setRegistrosdet(registrosdet.filter(
                            registrosdet => registrosdet.comprobante !== sComprobante
                            ));
            setTimeout(() => { // Agrega una función para que se ejecute después del tiempo de espera
                setUpdateTrigger(Math.random());//experimento
            }, 200);
      } else {
        console.log("❌ Cancelado");
        return; // Salimos si el usuario cancela
      }
    }
    const eliminarRegistroSeleccionado = async (sAnfitrion, sDocumentoId, sPeriodo, sComprobante, sElemento) => {
      const [COD, SERIE, NUMERO] = sComprobante.split('-');
      const datosEnvio = {
        periodo: sPeriodo,
        id_anfitrion: sAnfitrion,
        documento_id: sDocumentoId,
        r_cod: COD,
        r_serie: SERIE,
        r_numero: NUMERO,
        elemento: sElemento
      }
      //console.log('datosEnvio',datosEnvio);
  
      try {
          const response = await axios.delete(`${back_host}/ad_ventadel`, {
              data: datosEnvio
          });
  
          // Verifica la respuesta del backend
          if (response.data.success) {
            /*swal({
              text:"Venta se ha eliminado con exito",
              icon:"success",
              timer:"2000"
            });*/
            confirmDialog({
                    title: "Venta se ha eliminado con exito",
                    //message: `${sComprobante}`,
                    icon: "success", // success | error | info | warning
                    confirmText: "ACEPTAR"
                    //cancelText: "CERRAR",
            });
          } else {
            confirmDialog({
              title: "Eliminacion Denegada, solo ultimo del Periodo",
              icon: "error",
              confirmText: "ACEPTAR"
            });
            //console.log("No se pudo eliminar la venta, no es la ultima: " + response.data.message);
          }
      } catch (error) {
          //console.error("Error eliminando venta:", error);
            swal({
              text:"No se puede Eliminar Venta",
              icon:"error",
              timer:"2000"
            });
          
      }
  };
  
    const handleDeleteOrigen = async (sAnfitrion,sDocumentoId,sPeriodo,sLibro) => {
      const { value: selectedOrigen } = await swal2.fire({
        title: 'Eliminar registros',
        //text: 'Selecciona el origen para la eliminación masiva:',
        input: 'select',
        icon: 'warning',
        //color: 'orange',
        inputOptions: {
          EXCEL: 'EXCEL',
          SIRE: 'SIRE',
          MANUAL: 'MANUAL',
          // Agrega las opciones según los valores de "origen" de tu tabla
        },
        inputPlaceholder: 'Selecciona el origen',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        inputValidator: (value) => {
          return new Promise((resolve) => {
            if (value === '') {
              resolve('Debes seleccionar un origen');
            } else {
              resolve();
            }
          });
        },
      });
  
      // Si el usuario hace clic en "Eliminar" y selecciona un origen
      if (selectedOrigen) {
        // Aquí puedes realizar la lógica para eliminar registros masivamente con el origen seleccionado
        //console.log('Eliminar registros con origen:', selectedOrigen);
        await fetch(`${back_host}/asientomasivo/${sAnfitrion}/${sDocumentoId}/${sPeriodo}/${sLibro}/${selectedOrigen}`, {
          method:"DELETE"
        });
  
        setTimeout(() => { // Agrega una función para que se ejecute después del tiempo de espera
          setUpdateTrigger(Math.random());//experimento
        }, 200);
        console.log('eliminadooooo todo, ahora debemos recargar en 2do useeffect');
        cargaRegistro(valorVista,periodo_trabajo,contabilidad_trabajo,diaSel);
      }
    };
    
    const calcularSumatoriaMoneda = (columna, filtro) => {
      return registrosdet.reduce((acumulador, fila) => {
        // Verificar si el valor del campo de filtro coincide
        if (fila['r_moneda'] === filtro) {
          const valorColumna = fila[columna];
          // Verificar si el valor no es nulo y es numérico antes de sumarlo
          if (valorColumna !== null && !isNaN(valorColumna)) {
            return acumulador + parseFloat(valorColumna);
          }
        }
        return acumulador;
      }, 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    };  
    const calcularSumatoriaFilasMoneda = (filtro) => {
      return registrosdet.reduce((acumulador, fila) => {
        // Verificar si el valor del campo de filtro coincide
        if (fila['r_moneda'] === filtro) {
            return acumulador + 1;
        }
        return acumulador;
      }, 0);
    };  
  
  
    ///////////////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////////////
    const cargaRegistro = async (strHistorialValorVista,strHistorialPeriodo,strHistorialContabilidad, sDia) => {
      const periodoConsulta = strHistorialPeriodo || periodo_trabajo || params.periodo;
      const documentoConsulta = strHistorialContabilidad || contabilidad_trabajo || params.documento_id;
      const diaConsulta = sDia || "*";
      const cargaId = ++cargaRegistroSeq.current;

      if (!periodoConsulta || !documentoConsulta) {
        setRegistrosdet([]);
        setTabladet([]);
        return;
      }

      try {
        const response = await fetch(`${back_host}/ad_presupuesto/${periodoConsulta}/${params.id_anfitrion}/${documentoConsulta}/${diaConsulta}`);
        const result = await response.json();
        const registros = Array.isArray(result?.data) ? result.data : [];
        const registrosConReferencias = await completarReferenciasComprobante(registros, periodoConsulta, documentoConsulta);
        if (cargaId !== cargaRegistroSeq.current) {
          return;
        }
        setRegistrosdet(registrosConReferencias);
        setTabladet(registrosConReferencias);
      } catch (error) {
        if (cargaId !== cargaRegistroSeq.current) {
          return;
        }
        console.log("Error cargando presupuestos:", error);
        setRegistrosdet([]);
        setTabladet([]);
      }
    }

    const completarReferenciasComprobante = async (presupuestos, periodoConsulta, documentoConsulta) => {
      const requiereReferencia = presupuestos.some((presupuesto) => (
        !tieneComprobanteGenerado(presupuesto) && (presupuesto.estado || "P") === "C"
      ));

      if (!requiereReferencia) {
        return presupuestos;
      }

      try {
        const response = await fetch(`${back_host}/ad_venta/${periodoConsulta}/${params.id_anfitrion}/${documentoConsulta}/*`);
        const result = await response.json();
        const ventas = Array.isArray(result?.value)
          ? result.value
          : Array.isArray(result?.data)
            ? result.data
            : [];

        const comprobantes = ventas.filter((venta) => venta.r_cod !== "NV");

        return presupuestos.map((presupuesto) => {
          if (tieneComprobanteGenerado(presupuesto) || (presupuesto.estado || "P") !== "C") {
            return presupuesto;
          }

          const fechaGeneracionPresupuesto = presupuesto.ctrl_mod ? new Date(presupuesto.ctrl_mod).getTime() : null;
          const candidatosExactos = comprobantes.filter((venta) => {
            const fechaCreacionVenta = venta.ctrl_crea ? new Date(venta.ctrl_crea).getTime() : null;
            return (
              fechaGeneracionPresupuesto &&
              fechaCreacionVenta &&
              fechaGeneracionPresupuesto === fechaCreacionVenta &&
              String(venta.r_documento_id || "") === String(presupuesto.r_documento_id || "") &&
              Number(venta.r_monto_total || 0).toFixed(2) === Number(presupuesto.r_monto_total || 0).toFixed(2)
            );
          });

          const candidatosPorDatos = comprobantes.filter((venta) => (
            String(venta.r_documento_id || "") === String(presupuesto.r_documento_id || "") &&
            Number(venta.r_monto_total || 0).toFixed(2) === Number(presupuesto.r_monto_total || 0).toFixed(2)
          ));

          const candidato = candidatosExactos.length === 1
            ? candidatosExactos[0]
            : candidatosPorDatos.length === 1
              ? candidatosPorDatos[0]
              : null;

          if (!candidato) {
            return presupuesto;
          }

          const factElemento = candidato.elemento || 1;

          return {
            ...presupuesto,
            fact_cod: candidato.r_cod,
            fact_serie: candidato.r_serie,
            fact_num: candidato.r_numero,
            fact_elemento: factElemento,
            fact_comprobante: candidato.comprobante || `${candidato.r_cod}-${candidato.r_serie}-${candidato.r_numero}`,
            fact_comprobante_key: candidato.comprobante_key || `${candidato.r_cod}-${candidato.r_serie}-${candidato.r_numero}-${factElemento}`,
            fact_r_vfirmado: candidato.r_vfirmado,
            fact_cdr_pendiente: candidato.cdr_pendiente,
            fact_cdr_nivel: candidato.cdr_nivel,
          };
        });
      } catch (error) {
        console.log("No se pudieron completar referencias de comprobantes desde ventas:", error);
        return presupuestos;
      }
    };

    const marcarComprobanteGeneradoEnLista = (presupuesto, generado) => {
      const factCod = generado.r_cod;
      const factSerie = generado.r_serie;
      const factNum = generado.r_numero;
      const factElemento = generado.elemento || 1;

      if (!factCod || !factSerie || !factNum) {
        return;
      }

      const coincidePresupuesto = (item) => (
        (item.r_cod || "NV") === (presupuesto.r_cod || "NV") &&
        (item.r_serie || "0001") === (presupuesto.r_serie || "0001") &&
        item.r_numero === presupuesto.r_numero &&
        String(item.elemento || 1) === String(presupuesto.elemento || 1)
      );

      const aplicarReferencia = (item) => coincidePresupuesto(item)
        ? {
            ...item,
            fact_cod: factCod,
            fact_serie: factSerie,
            fact_num: factNum,
            fact_elemento: factElemento,
            fact_comprobante: `${factCod}-${factSerie}-${factNum}`,
            fact_comprobante_key: `${factCod}-${factSerie}-${factNum}-${factElemento}`,
          }
        : item;

      setRegistrosdet((prev) => prev.map(aplicarReferencia));
      setTabladet((prev) => prev.map(aplicarReferencia));
    };
    //////////////////////////////////////
    
   
    const navigate = useNavigate();
    //Para recibir parametros desde afuera
    const params = useParams();
  
    const actualizaValorFiltro = e => setValorBusqueda(e.target.value);
    const data = useMemo(() => {
      const busqueda = valorBusqueda.trim().toLocaleLowerCase("es");
      return registrosdet.map(normalizarPresupuestoListado).filter((row) => (
        (estadoFiltro === "*" || (row.estado || "P") === estadoFiltro) &&
        (cpeFiltro === "*" || tieneComprobanteGenerado(row) === (cpeFiltro === "con")) &&
        (!busqueda || [row.numero, row.cliente, row.r_documento_id, row.autor, row.glosa,
          comprobanteGeneradoPresupuesto(row), row.trabajos_busqueda, ...row.trabajos.flatMap(trabajo => [trabajo.numero, trabajo.descripcion])]
          .some(value => String(value || "").toLocaleLowerCase("es").includes(busqueda)))
      ));
    }, [registrosdet, valorBusqueda, estadoFiltro, cpeFiltro]);

    const exportarPresupuestosExcel = () => {
      if (!data.length) {
        swal2.fire({
          title: "Sin datos para exportar",
          text: "No hay presupuestos con los filtros actuales.",
          icon: "info",
          confirmButtonText: "ACEPTAR",
          color: palette.text,
          background: palette.surface,
        });
        return;
      }

      const filas = data.map((presupuesto, index) => ({
        "#": index + 1,
        Presupuesto: presupuesto.numero,
        Fecha: presupuesto.fecha,
        Cliente: presupuesto.cliente,
        "Documento cliente": presupuesto.r_documento_id || "",
        Estado: (presupuesto.estado || "P") === "C" ? "Cerrado" : "Pendiente",
        Moneda: presupuesto.moneda,
        Total: presupuesto.total,
        Servicios: presupuesto.servicios_count,
        Autor: presupuesto.autor,
        Glosa: presupuesto.glosa || "",
        CPE: comprobanteGeneradoPresupuesto(presupuesto) || "",
      }));
      const periodoArchivo = String(periodo_trabajo || params.periodo || "periodo").replace(/[^a-zA-Z0-9-]/g, "");
      const razonSocial = contabilidad_nombre || contabilidad_trabajo || params.documento_id || "empresa";
      const nombreArchivo = String(razonSocial).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9_-]/g, "_");
      const titulo = `CONTROL DE PRESUPUESTOS - ${razonSocial} ${periodo_trabajo || params.periodo || ""}`;
      const worksheet = XLSX.utils.aoa_to_sheet([
        [titulo],
        [`Periodo: ${periodo_trabajo || params.periodo || ""}`, `Presupuestos: ${filas.length}`, valorBusqueda.trim() ? "Búsqueda aplicada" : "Listado filtrado"],
        [],
      ]);
      XLSX.utils.sheet_add_json(worksheet, filas, { origin: "A4" });
      worksheet["!cols"] = Object.keys(filas[0]).map((key) => ({
        wch: Math.min(Math.max(key.length + 2, ...filas.map((fila) => String(fila[key] ?? "").length + 2)), 42),
      }));
      worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: Object.keys(filas[0]).length - 1 } }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Presupuestos");
      XLSX.writeFile(workbook, `control_presupuestos_${nombreArchivo}-${periodoArchivo}.xlsx`);

      swal2.fire({
        title: "Excel generado",
        text: `${filas.length} presupuesto${filas.length === 1 ? "" : "s"} exportado${filas.length === 1 ? "" : "s"}.`,
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
        color: palette.text,
        background: palette.surface,
      });
    };

    useEffect(() => {
      setPaginaReset(value => !value);
    }, [valorBusqueda, estadoFiltro, cpeFiltro, diaSel, periodo_trabajo, contabilidad_trabajo]);

    const cargaPermisosMenuComando = async(idMenu)=>{
      if (params.id_anfitrion === params.id_invitado){
        setPVenta0101(true); //nuevo
        setPVenta0102(true); //modificar
        setPVenta0103(true); //eliminar
        setPVenta0104(true); //eliminar masivo
      }
      else{
          //Realiza la consulta a la API de permisos
          fetch(`${back_host}/seguridad/${params.id_anfitrion}/${params.id_invitado}/${idMenu}`, {
            method: 'GET'
          })
          .then(response => response.json())
          .then(permisosData => {
            // Guarda los permisos en el estado
            setPermisosComando(permisosData);
            console.log(permisosComando);
            let tienePermiso;
            // Verifica si existe el permiso de acceso 'ventas'
            tienePermiso = permisosData.some(permiso => permiso.id_comando === '20-01'); //Nuevo
            if (tienePermiso) {
              setPVenta0101(true);
            }
  
            tienePermiso = permisosData.some(permiso => permiso.id_comando === '20-02'); //Modificar
            if (tienePermiso) {
              setPVenta0102(true);
            }else {setPVenta0102(false);}
  
            tienePermiso = permisosData.some(permiso => permiso.id_comando === '20-03'); //Eliminar
            if (tienePermiso) {
              setPVenta0103(true);
            }else {setPVenta0103(false);}
  
            tienePermiso = permisosData.some(permiso => permiso.id_comando === '20-04'); //Eliminar Masivo
            if (tienePermiso) {
              setPVenta0104(true);
            }else {setPVenta0104(false);}
            ////////////////////////////////////////////////
            
            ////////////////////////////////////////////////
  
            //setUpdateTrigger(Math.random());//experimento
          })
          .catch(error => {
            console.log('Error al obtener los permisos:', error);
          });
      }
    }
  
    //////////////////////////////////////////////////////////
    useEffect( ()=> {
        
        // Realiza acciones cuando isAuthenticated cambia
        //Verificar historial periodo 
        const st_periodo_trabajo = sessionStorage.getItem('periodo_trabajo');
        
        //New Cargar Lista, con sugerencia de foco inicial
        if (st_periodo_trabajo===null || st_periodo_trabajo===''){
            //en caso no haya periodos ni ruc registrados, no tiene porque cargar
          cargaPeriodosAnfitrion(params.periodo);
          setPeriodoTrabajo(params.periodo);
        }else{
          //en caso haya periodos y rucs, debe respetar el ambiente de trabajo anterior
          //cuidado con eliminar un ruc, el ambiente de trabajo podria desaparecer y generar bug ... ****
          cargaPeriodosAnfitrion(st_periodo_trabajo);
          setPeriodoTrabajo(st_periodo_trabajo);
        }
  
        //Verifica historial contabilidad
        const st_contabilidad_trabajo = sessionStorage.getItem('contabilidad_trabajo');
        const st_contabilidad_nombre = sessionStorage.getItem('contabilidad_nombre');
        //New Cargar Lista, con sugerencia de foco inicial
        if (st_contabilidad_trabajo===null || st_contabilidad_nombre===''){
          //en caso no haya periodos ni ruc registrados, no tiene porque cargar
          cargaContabilidadesAnfitrion(params.documento_id,st_contabilidad_nombre);
          setContabilidadTrabajo(params.documento_id);
        }else{
          //en caso haya periodos y rucs, debe respetar el ambiente de trabajo anterior
          //cuidado con eliminar un ruc, el ambiente de trabajo podria desaparecer y generar bug ... ****
          cargaContabilidadesAnfitrion(st_contabilidad_trabajo,st_contabilidad_nombre);
          setContabilidadTrabajo(st_contabilidad_trabajo);
          setContabilidadNombre(st_contabilidad_nombre);
        }
        
        /////////////////////////////
        //NEW codigo para autenticacion y permisos de BD
        if (isAuthenticated && user && user.email) {
          cargaPermisosMenu(); //carga permisos menus
        }
  
    },[isAuthenticated, user]) //Aumentamos IsAuthenticated y user
  
    /*useEffect(() => {
      console.log('useEffect location.pathname: ',location.pathname);
      fetchTotalVentas();
    }, [location.pathname]);*/
  
    /*useEffect( ()=> {
      
        //Carga por cada cambio de seleccion en toggleButton
        console.log('2do useeffect periodo_trabajo: ',periodo_trabajo);
  
        //Verifica historial id_libro
        const st_id_libro = sessionStorage.getItem('id_libro');
        const st_valorVista = (sessionStorage.getItem('valorVista') || 'ventas'); //new para el toggleButton
  
        if (st_id_libro) {
          //Establecer valor historial al toggleButton
          setValorLibro(st_id_libro);
          setValorVista(st_valorVista);
        }
  
        if (st_valorVista===null || st_valorVista===undefined || st_valorVista===''){
        cargaPermisosMenuComando('20'); //Por default, la 1era vez
        setValorVista('ventas'); //Por default, la 1era vez
        //st_valorVista = 'ventas'; //new 
        }else{
        setValorVista(st_valorVista);
        }
        if (st_valorVista === 'ventas') {cargaPermisosMenuComando('20');}
  
        //fcuando carga x primera vez, sale vacio ... arreglar esto
        cargaRegistro(st_valorVista,periodo_trabajo,contabilidad_trabajo, diaSel);
      
        fetchTotalVentas();
    },[updateTrigger, diaSel]) //Aumentamos
  
    useEffect( ()=> {
      //Carga de Registros con permisos
      console.log('3ero useeffect periodo_trabajo: ',periodo_trabajo);
  
      const st_id_libro = sessionStorage.getItem('id_libro');
      const st_valorVista = sessionStorage.getItem('valorVista'); //para el toggleButton
      console.log('3ero useeffect st_id_libro: ',st_id_libro);
      if (st_id_libro) {
        //Establecer valor historial al toggleButton
        setValorLibro(st_id_libro);
        setValorVista(st_valorVista);
      }
  
      const st_periodo_trabajo = sessionStorage.getItem('periodo_trabajo'); //parametro necesario
      const st_contabilidad_trabajo = sessionStorage.getItem('contabilidad_trabajo'); //parametro necesario
  
      //Secundario despues de seleccion en toggleButton
      let columnasEspecificas;
      if (st_valorVista===null || st_valorVista===undefined || st_valorVista===''){
        columnasEspecificas = AdminVentasColumnas;
      }else{
        columnasEspecificas = 
            st_valorVista === 'ventas' ? AdminVentasColumnas
          : AdminCajaColumnas;
      }
  
      cargaColumnasComunes();        
      const combinado = [...columnasComunes, ...columnasEspecificas];
  
      //setColumnas([...columnasComunes, ...columnasEspecificas]);
      // Reordenamos (columna 4 → posición 7) Estetica para GRE
      //const ordenado = moveColumn(combinado, 3, 5); 
  
      // Finalmente seteamos
      setColumnas(combinado);    
  
      //cuando carga x primera vez, sale vacio ... arreglar esto
      cargaRegistro(st_valorVista,periodo_trabajo,contabilidad_trabajo, diaSel); //new cambio
  
      //Datos listos en caso de volver por aqui, para envio
      setDatosCarga(prevState => ({ ...prevState, id_anfitrion: params.id_anfitrion }));
      setDatosCarga(prevState => ({ ...prevState, periodo: st_periodo_trabajo }));
      setDatosCarga(prevState => ({ ...prevState, documento_id: st_contabilidad_trabajo }));
      setDatosCarga(prevState => ({ ...prevState, id_libro: st_id_libro }));
      setDatosCarga(prevState => ({ ...prevState, id_invitado: params.id_invitado }));
  
    },[permisosComando, pVenta0101, diaSel]) //Solo cuando este completo estado
    */

    //////////////////////////////////////////////////////////
    const cargaColumnasComunes = () =>{
      //Verificar que el resto de permisos de otros libros siempre esten FALSE
      //Solo el libro en cuestion, validara TRUE OR FALSE
  
      columnasComunes = [
        {
          name: '',
          width: isSmallScreen ?  '40px' : '30px',
          cell: (row) => (
            (pVenta0103) && (row.r_cod !== 'NV') ? 
            (
              <AdminSunatIcon
                comprobante_key={row.comprobante_key}
                comprobante={row.comprobante}
                cdr_pendiente={row.cdr_pendiente} //new
                elemento={row.elemento}
                firma={row.r_vfirmado}
                documentoId={params.documento_id}
                periodoTrabajo={periodo_trabajo}
                idAnfitrion={params.id_anfitrion}
                contabilidadTrabajo={contabilidad_trabajo}
                backHost={back_host}
                onRefresh={() => setUpdateTrigger(Math.random())} // ✅ refresca al cerrar el modal
                size={26}
              />
            ) : null
          ),
          allowOverflow: true,
          button: true,
        },
        {
          name: '',
          width: isSmallScreen ?  '40px' : '30px',
          cell: (row) => (
            (pVenta0102) && (row.r_vfirmado == null) ?
            (
              <DriveFileRenameOutlineIcon
                onClick={() => handleUpdate(row.comprobante_key,false)}
                style={{
                  cursor: 'pointer',
                  color: 'skyblue',
                  transition: 'color 0.3s ease',
                }}
              />
            )  
            : 
            (
              <FindInPageIcon
                onClick={() => handleUpdate(row.comprobante_key,true)}
                style={{
                  cursor: 'pointer',
                  color: 'gray',
                  transition: 'color 0.3s ease',
                }}
              />
  
            )
          ),
          allowOverflow: true,
          button: true,
        },
        {
          name: '',
          width: isSmallScreen ?  '40px' : '30px',
          cell: (row) => (
            (pVenta0103) && (row.r_vfirmado == null) ?
            (
              <DeleteIcon
                onClick={() => handleDelete(row.comprobante_key, row.elemento)}
                style={{
                  cursor: 'pointer',
                  color: 'orange',
                  transition: 'color 0.3s ease',
                }}
              />
            ) 
            : 
            (
              <ContentCopyIcon
                onClick={() => {
                    setShowModalMostrarClonar(true);
                    setValorComprobante(row.comprobante_key);
                    //clonarVenta(row.comprobante_key);
                    }
                  }
                style={{
                  cursor: 'pointer',
                  //color: 'primary',
                  transition: 'color 0.3s ease',
                }}
              />
  
            )
  
          ),
          allowOverflow: true,
          button: true,
        },
        {
          name: '',
          width: isSmallScreen ?  '40px' : '30px',
          cell: (row) => (
            (pVenta0103) && (row.r_cod !== 'NV') ? 
            (
              <AdminSunatGreIcon
                comprobante_gre={row.gre_ref}
                comprobante_venta={row.comprobante}
                destinatario_venta={{'destinatario_ruc_dni':row.r_documento_id, 'destinatario_razon_social':row.r_razon_social}}
                firma={row.gre_vfirmado}
                documentoId={params.documento_id}
                periodoTrabajo={periodo_trabajo}
                idAnfitrion={params.id_anfitrion}
                idInvitado={params.id_invitado} //new para GRE, identifica serie autorizada 
                contabilidadTrabajo={contabilidad_trabajo}
                backHost={back_host}
                onRefresh={() => setUpdateTrigger(Math.random())} // ✅ refresca al cerrar el modal
                size={26}
              />
            ) 
            : 
            ( //Auxiliamos con clonar para pedidos, en caso modo simple
            (pVenta0103) && (row.r_cod === 'NV') ? 
            (
              <ContentCopyIcon
                onClick={() => {
                    setShowModalMostrarClonar(true);
                    setValorComprobante(row.comprobante_key);
                    //clonarVenta(row.comprobante_key);
                    }
                  }
                style={{
                  cursor: 'pointer',
                  //color: 'primary',
                  transition: 'color 0.3s ease',
                }}
              />
            )
            : null
            )
          ),
          allowOverflow: true,
          button: true,
        },
  
      ];
    }
    const cargaPeriodosAnfitrion = (strHistorialPeriodo) =>{
      axios
      .get(`${back_host}/usuario/periodos/${params.id_anfitrion}`)
      .then((response) => {
        setPeriodosSelect(response.data);
        //console.log(response.data);
  
        if (strHistorialPeriodo === '' || strHistorialPeriodo === null){
          //Establecer 1er elemento en select
          if (response.data.length > 0) {
            setPeriodoTrabajo(response.data[0].periodo); 
            sessionStorage.setItem('periodo_trabajo',response.data[0].periodo);
          }
        }
        else{//Establecer elemento historial
          setPeriodoTrabajo(strHistorialPeriodo); 
          console.log('periodo_trabajo: ', periodo_trabajo);
          console.log('strHistorialPeriodo: ', strHistorialPeriodo);
        }
      })
      .catch((error) => {
          console.log(error);
      });
    }
    const cargaContabilidadesAnfitrion = (strHistorialContabilidad,strHistorialContabilidadNombre) =>{
      axios
      //Aqui debemos agregar restriccion de contabilidad por(usuario auxiliar)
      .get(`${back_host}/usuario/contabilidades/${params.id_anfitrion}/${params.id_invitado}`)
      .then((response) => {
        setContabilidadesSelect(response.data);
        if (strHistorialContabilidad === '' || strHistorialContabilidad === null){
          //Establecer 1er elemento en select
          if (response.data.length > 0) {
            setContabilidadTrabajo(response.data[0].documento_id); 
            setContabilidadNombre(response.data[0].razon_social); 
            sessionStorage.setItem('contabilidad_trabajo',response.data[0].documento_id);
          }
        }
        else{//Establecer elemento historial
          setContabilidadTrabajo(strHistorialContabilidad); 
          setContabilidadNombre(strHistorialContabilidadNombre); 
        }
      })
      .catch((error) => {
          console.log(error);
      });
    }
  
    const cargaPermisosMenu = async()=>{
      //console.log(`${back_host}/seguridadmenu/${params.id_anfitrion}/${params.id_invitado}`);
      //En caso de anfitrion debemos establecer permisos en true, sin consumir api de consulta 
      if (params.id_anfitrion===params.id_invitado){
        setPermisoVentas(true);
      }
      else{
          //Realiza la consulta a la API de permisos, puro Menu (obtenerTodosMenu)
          fetch(`${back_host}/seguridadmenu/${params.id_anfitrion}/${params.id_invitado}`, {
            method: 'GET',
            //headers: {
            //  'Authorization': 'TOKEN_DE_AUTORIZACION' // Si es necesario
            // }
          })
          .then(response => response.json())
          .then(permisosData => {
            // Guarda los permisos en el estado
            setPermisos(permisosData);
        
            let tienePermiso;
            // Verifica si existe el permiso de acceso 'ventas'
            tienePermiso = permisosData.some(permiso => permiso.id_menu === '01');
            if (tienePermiso) {
              setPermisoVentas(true);
            }
          })
          .catch(error => {
            console.log('Error al obtener los permisos:', error);
          });
      }
    };
  
    //////////////////////////////////////////////////////////
    const abrirCerrarModal = ()=>{
      setAbierto(!abierto);
    };
    const [abierto,setAbierto] = useState(false);
    const modalStyles={
      position:'absolute',
      top:'0%',
      left:'0%',
      background:'gray',
      border:'2px solid #000',
      padding:'16px 32px 24px',
      width:'100',
      minHeight: '50px'
      //transform:'translate(0%,0%)'
    };
    const handleCerrar = (updatedData) => {
      setDatosPopUp(updatedData); // Actualiza los datos con los datos modificados
      setAbierto(false); // Cierra el modal
    };
    //////////////////////////////////////////////////////
    const generaVenta = async () => {
      try {
        //dia
        const response = await axios.post(`${back_host}/ad_venta`, {
          id_anfitrion: params.id_anfitrion,
          documento_id: params.documento_id,
          periodo: periodo_trabajo,
          id_invitado: params.id_invitado,
          fecha: obtenerFecha(periodo_trabajo,true,diaSel),
        });
        
  
        if (response.data.success) {
          const sComprobanteAbierto = 'NP-0001-' + response.data.r_numero+'-1'; //aumentamos elemento
          const sComprobanteAbiertoRef = '-'; //modo directo sin ref
          //enviamos la edicion del registro abierto
          navigate(`/ad_venta/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo}/${contabilidad_trabajo}/${sComprobanteAbierto}/${sComprobanteAbiertoRef}`);
        } else {
          //setError(response.data.message);
          console.log(response.data.message);
        }
      } catch (err) {
        //setError('Error al crear el pedido.');
        console.log('Error al crear el pedido.');
      }    
    };
    const clonarVenta = async (sComprobante) => {
      try {
        //console.log('dia sel para clonado: ... ', diaSel);
        const [COD, SERIE, NUMERO] = sComprobante.split('-');
  
         // Generar nuevo periodo desde fecha_clon
        const periodo_nuevo = fecha_clon.substring(0, 7);
        console.log('periodo_nuevo: ', periodo_nuevo);
  
        //dia
        const response = await axios.post(`${back_host}/ad_ventaclon`, {
          id_anfitrion: params.id_anfitrion,
          documento_id: params.documento_id,
          periodo: periodo_trabajo,
          id_invitado: params.id_invitado,
          fecha: fecha_clon,
          r_cod: COD,
          r_serie: SERIE,
          r_numero: NUMERO
        });
        
  
        if (response.data.success) {
          const sComprobanteAbierto = 'NP-0001-' + response.data.r_numero+'-1';//aumentamos elemento
          const sComprobanteAbiertoRef = sComprobante; //modo clonar con ref
          //enviamos la edicion del registro abierto
          navigate(`/ad_venta/${params.id_anfitrion}/${params.id_invitado}/${periodo_nuevo}/${contabilidad_trabajo}/${sComprobanteAbierto}/${sComprobanteAbiertoRef}`);
        } else {
          //setError(response.data.message);
          console.log(response.data.message);
        }
      } catch (err) {
        //setError('Error al crear el pedido.');
        console.log('Error al crear el pedido.');
      }    
    };
  
    const obtenerFecha = (periodo,bformatoBD,sDia) => {
      // Obtener el mes y año del parámetro "periodo" en formato "AAAA-MM"
      const [year, month] = periodo.split('-').map(Number);
    
      // Obtener la fecha actual
      const fechaActual = new Date();
      
      /*if (sDia!==''){
          //restamos 1 al mes, pinche manejo de fecha js
        const fechaSeleccionada = new Date(year, month-1, sDia); // Al pasar 0 en el día, se obtiene el último día del mes
        return formatearFecha(fechaSeleccionada,bformatoBD,sDia); // Retorna la fecha actual formateada
      }else{
        return formatearFecha(fechaActual,bformatoBD,sDia); // Retorna la fecha actual formateada
      }*/
  
      if (sDia==='' || sDia==='*'){
        return formatearFecha(fechaActual,bformatoBD,sDia); // Retorna la fecha actual formateada
      }else{
        //restamos 1 al mes, pinche manejo de fecha js
        const fechaSeleccionada = new Date(year, month-1, sDia); // Al pasar 0 en el día, se obtiene el último día del mes
        return formatearFecha(fechaSeleccionada,bformatoBD,sDia); // Retorna la fecha actual formateada
      }
    };
  
    // Función para formatear la fecha en DD/MM/YYYY
    const formatearFecha = (fecha,bformatoBD,sDia) => {
      let dia;
      if (sDia==='' || sDia==='*'){
        dia = String(fecha.getDate()).padStart(2, '0');
      }
      else {
        dia = sDia;      
      }
      
      const mes = String(fecha.getMonth() + 1).padStart(2, '0'); // Los meses en JavaScript son base 0
      const anio = fecha.getFullYear();
      if (bformatoBD) {
        return `${anio}-${mes}-${dia}`;
      }else{
        return `${dia}/${mes}/${anio}`;
      }
    };
  
  const handleDayFilter = (selectedDay) => {
    const dia = selectedDay === '*' ? '*' : selectedDay.toString().padStart(2, '0');
    setDiaSel(dia);
  };
    
  const [totalVentas, setTotalVentas] = useState(0);
  const [isSuper, setIsSuper] = useState(false);
  const superDesdeBienvenida = [superUsuarioProp, sessionStorage.getItem("super")].some((value) => (
    ["1", "true", "t", "s", "si", "sí", "yes", "y"].includes(String(value || "0").trim().toLowerCase())
  ));
  const coincideId = (anfitrion, invitado) => Boolean(anfitrion && invitado) && (
    String(anfitrion).trim().toLowerCase() === String(invitado).trim().toLowerCase()
  );
  const esAnfitrion = (
    coincideId(params.id_anfitrion, params.id_invitado) ||
    coincideId(idAnfitrion, idInvitado)
  );
  const puedeVerTotalPresupuestos = esAnfitrion || isSuper || superDesdeBienvenida;
  const fetchTotalVentas = async (signal) => {
      try {
        const res = await axios.get(`${back_host}/ad_ventatotal/${periodo_trabajo}/${params.id_anfitrion}/${params.id_invitado}/${contabilidad_trabajo}/${diaSel}`, { signal });
        if (signal?.aborted) return;
        setTotalVentas(res.data.total);
        setIsSuper(res.data.super);
      } catch (error) {
        if (signal?.aborted) return;
        console.error('Error al obtener total de ventas', error);
      }
  }; 

  useEffect(() => {
    if (!periodo_trabajo || !contabilidad_trabajo) {
      setIsSuper(false);
      return undefined;
    }

    const controller = new AbortController();
    fetchTotalVentas(controller.signal);
    return () => controller.abort();
  }, [periodo_trabajo, contabilidad_trabajo, diaSel, params.id_anfitrion, params.id_invitado]);
  const [recaudaciones, setRecaudaciones] = useState([]);
  const [showModalMostrarRecaudacion, setShowModalMostrarRecaudacion] = useState(false);
  const [showModalMostrarClonar, setShowModalMostrarClonar] = useState(false);
  const handleClickTotal = (periodo,id_anfitrion,documento_id,dia) => {
    setShowModalMostrarRecaudacion(true);
    axios.get(`${back_host}/ad_ventarecaudacion/${periodo}/${id_anfitrion}/${documento_id}/${dia}`)
            .then(res => {
              if (res.data.success) {
                setRecaudaciones(res.data.data);
                console.log('Recaudaciones: ', res.data.data);
              }
            })
            .catch(err => console.error(err));
  };
    const handleOpenLink = (url) => {
      if (url) {
        window.open(url, "_blank"); 
        // "_blank" abre en nueva pestaña
        // "_self" reemplaza la pestaña actual
      } else {
        alert("⚠️ No hay documento disponible");
      }
    };

    const obtenerFechaPeriodo = (periodo, sDia) => {
      const [year, month] = String(periodo || "").split("-").map(Number);
      if (!year || !month) {
        return obtenerFecha(periodo, true, sDia);
      }

      const fechaActual = new Date();
      const periodoActual = `${fechaActual.getFullYear()}-${String(fechaActual.getMonth() + 1).padStart(2, "0")}`;
      const dia = sDia && sDia !== "*"
        ? String(sDia).padStart(2, "0")
        : periodo === periodoActual
          ? String(fechaActual.getDate()).padStart(2, "0")
          : "01";

      return `${year}-${String(month).padStart(2, "0")}-${dia}`;
    };

    const extraerNumeroPresupuestoCreado = (payload) => {
      const data = Array.isArray(payload?.data) ? payload.data[0] : payload?.data;
      return payload?.r_numero || data?.r_numero || data?.numero || payload?.numero || "";
    };

    const handleNuevoPresupuesto = async () => {
      const periodoActual = periodo_trabajo || params.periodo;
      const documentoActual = contabilidad_trabajo || params.documento_id;
      const fecha = obtenerFechaPeriodo(periodoActual, diaSel);

      try {
        const response = await axios.post(`${back_host}/ad_presupuesto`, {
          id_anfitrion: params.id_anfitrion,
          documento_id: documentoActual,
          periodo: periodoActual,
          id_invitado: params.id_invitado,
          fecha,
        });

        if (!response.data.success) {
          throw new Error(response.data.message || "No se pudo crear el presupuesto.");
        }

        const rNumero = extraerNumeroPresupuestoCreado(response.data);

        if (!rNumero) {
          throw new Error("El presupuesto fue creado, pero el API no devolvio el numero.");
        }

        navigate(`/ad_ventapresupuesto/${params.id_anfitrion}/${params.id_invitado}/${periodoActual}/${documentoActual}/${rNumero}/edit`);
      } catch (error) {
        console.log("Error creando presupuesto:", error);
        alert(error.response?.data?.message || error.message || "No se pudo crear el presupuesto.");
      }
    };

    const handleEditPresupuesto = (presupuesto) => {
      navigate(`/ad_ventapresupuesto/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo || params.periodo}/${contabilidad_trabajo || params.documento_id}/${presupuesto.r_numero}/edit`);
    };

    const handleViewPresupuesto = (presupuesto) => {
      navigate(`/ad_ventapresupuesto/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo || params.periodo}/${contabilidad_trabajo || params.documento_id}/${presupuesto.r_numero}/view`);
    };

    const handlePdfPresupuesto = async (presupuesto) => {
      try {
        const response = await fetch(`${back_host}/ad_presupuesto/full/${periodo_trabajo || params.periodo}/${params.id_anfitrion}/${contabilidad_trabajo || params.documento_id}/${presupuesto.r_cod || "NV"}/${presupuesto.r_serie || "0001"}/${presupuesto.r_numero}/${presupuesto.elemento || 1}`);
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "No se pudo cargar el presupuesto para PDF.");
        }

        const url = await createPresupuestoPdf(normalizarPresupuestoFull(data.data), {
          modo: "cliente",
          documentoId: contabilidad_trabajo || params.documento_id,
        });
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.log("Error generando PDF de presupuesto:", error);
        alert(error.message || "No se pudo generar el PDF.");
      }
    };

    const cargarSeriesEmision = async (documentoId, rCod) => {
      const response = await axios.get(`${back_host}/ad_ventaseries/${params.id_anfitrion}/${documentoId}/${params.id_invitado}/${rCod}`);
      return Array.isArray(response.data?.data) ? response.data.data : [];
    };

    const handleGenerarComprobantePresupuesto = async (presupuesto) => {
      if (tieneComprobanteGenerado(presupuesto)) {
        return;
      }

      const periodoActual = periodo_trabajo || params.periodo;
      const documentoActual = contabilidad_trabajo || params.documento_id;

      try {
        const tipoResult = await swal2.fire({
          title: "Generar comprobante",
          text: `${presupuesto.numero || numeroPresupuesto(presupuesto)} - ${presupuesto.cliente || presupuesto.r_razon_social || "Sin cliente"}`,
          input: "select",
          inputOptions: {
            "01": "Factura",
            "03": "Boleta",
          },
          inputValue: presupuesto.r_id_doc === "1" ? "03" : "01",
          showCancelButton: true,
          confirmButtonText: "CONTINUAR",
          cancelButtonText: "CANCELAR",
          inputValidator: (value) => (!value ? "Selecciona el tipo de comprobante." : undefined),
        });

        if (!tipoResult.isConfirmed) {
          return;
        }

        const rCodEmitir = tipoResult.value;
        const series = await cargarSeriesEmision(documentoActual, rCodEmitir);

        if (series.length === 0) {
          throw new Error(`No hay series autorizadas para ${rCodEmitir}.`);
        }

        const serieOptions = series.reduce((acc, item) => {
          if (item.r_serie) {
            acc[item.r_serie] = item.r_serie;
          }
          return acc;
        }, {});

        const serieResult = await swal2.fire({
          title: "Serie autorizada",
          input: "select",
          inputOptions: serieOptions,
          inputValue: series[0]?.r_serie || "",
          showCancelButton: true,
          confirmButtonText: "GENERAR",
          cancelButtonText: "CANCELAR",
          inputValidator: (value) => (!value ? "Selecciona la serie." : undefined),
        });

        if (!serieResult.isConfirmed) {
          return;
        }

        const response = await axios.post(`${back_host}/ad_presupuesto/comprobante`, {
          id_anfitrion: params.id_anfitrion,
          documento_id: documentoActual,
          periodo: periodoActual,
          id_invitado: params.id_invitado,
          fecha: obtenerFechaPeriodo(periodoActual, diaSel),
          origen: {
            r_cod: presupuesto.r_cod || "NV",
            r_serie: presupuesto.r_serie || "0001",
            r_numero: presupuesto.r_numero,
            elemento: presupuesto.elemento || 1,
          },
          destino: {
            r_cod_emitir: rCodEmitir,
            r_serie_emitir: serieResult.value,
          },
          cliente: {
            r_id_doc: presupuesto.r_id_doc || "6",
            r_documento_id: presupuesto.r_documento_id,
            r_razon_social: presupuesto.r_razon_social,
            r_direccion: presupuesto.r_direccion,
          },
          pago: {
            r_moneda: presupuesto.r_moneda || "PEN",
            r_forma_pago_id: presupuesto.r_forma_pago_id || "Contado",
            dias_credito: presupuesto.dias_credito || 0,
            efectivo: null,
            vuelto: 0,
            forma_pago2: null,
            efectivo2: 0,
          },
          id_producto: "0000",
          cont_und_default: "ZZ",
        });

        if (!response.data?.success) {
          throw new Error(response.data?.message || "No se pudo generar el comprobante.");
        }

        const generado = response.data?.data || response.data;
        const comprobanteGenerado = `${generado.r_cod}-${generado.r_serie}-${generado.r_numero}-${generado.elemento || 1}`;

        marcarComprobanteGeneradoEnLista(presupuesto, generado);
        await cargaRegistro("presupuestos", periodoActual, documentoActual, diaSel);
        marcarComprobanteGeneradoEnLista(presupuesto, generado);

        const abrirResult = await swal2.fire({
          title: "Comprobante generado",
          text: comprobanteGenerado,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "ABRIR",
          cancelButtonText: "QUEDARSE",
        });

        if (abrirResult.isConfirmed) {
          navigate(`/ad_venta/${params.id_anfitrion}/${params.id_invitado}/${periodoActual}/${documentoActual}/${comprobanteGenerado}/view`);
        }
      } catch (error) {
        console.log("Error generando comprobante desde presupuesto:", error);
        swal2.fire({
          title: "No se pudo generar",
          text: error.response?.data?.message || error.message || "Error interno.",
          icon: "error",
          confirmButtonText: "ACEPTAR",
        });
      }
    };

    const renderSunatComprobantePresupuesto = (presupuesto) => {
      if (!tieneComprobanteGenerado(presupuesto)) {
        return null;
      }

      const factElemento = presupuesto.fact_elemento || 1;
      const factComprobante = comprobanteGeneradoPresupuesto(presupuesto);
      const factComprobanteKey = comprobanteGeneradoKeyPresupuesto(presupuesto);
      const periodoActual = periodo_trabajo || params.periodo;
      const documentoActual = contabilidad_trabajo || params.documento_id;

      return (
        <AdminSunatIcon
          comprobante_key={factComprobanteKey}
          comprobante={factComprobante}
          cdr_pendiente={presupuesto.fact_cdr_pendiente}
          elemento={factElemento}
          firma={presupuesto.fact_r_vfirmado}
          documentoId={documentoActual}
          periodoTrabajo={periodoActual}
          idAnfitrion={params.id_anfitrion}
          contabilidadTrabajo={documentoActual}
          backHost={back_host}
          onRefresh={() => setUpdateTrigger(Math.random())}
          size={26}
          cdr_nivel={presupuesto.fact_cdr_nivel}
        />
      );
    };

    const handleOpenComprobanteGenerado = (presupuesto) => {
      const comprobanteKey = comprobanteGeneradoKeyPresupuesto(presupuesto);

      if (!comprobanteKey) {
        return;
      }

      navigate(`/ad_venta/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo || params.periodo}/${contabilidad_trabajo || params.documento_id}/${comprobanteKey}/view`);
    };

    const handleDeletePresupuesto = async (presupuesto) => {
      if ((presupuesto.estado || "P") !== "P") {
        alert("Solo se pueden eliminar presupuestos pendientes.");
        return;
      }

      const result = await confirmDialog({
        title: "Eliminar presupuesto?",
        message: `${presupuesto.numero || numeroPresupuesto(presupuesto)} - ${presupuesto.cliente || presupuesto.r_razon_social || "Sin cliente"}`,
        icon: "warning",
        confirmText: "ELIMINAR",
        cancelText: "Cancelar",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const periodoActual = periodo_trabajo || params.periodo;
        const documentoActual = contabilidad_trabajo || params.documento_id;
        const response = await fetch(`${back_host}/ad_presupuesto/${periodoActual}/${params.id_anfitrion}/${documentoActual}/${presupuesto.r_cod || "NV"}/${presupuesto.r_serie || "0001"}/${presupuesto.r_numero}/${presupuesto.elemento || 1}`, {
          method: "DELETE",
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "No se pudo eliminar el presupuesto.");
        }

        const mismoPresupuesto = (item) => (
          item.r_cod === (presupuesto.r_cod || "NV") &&
          item.r_serie === (presupuesto.r_serie || "0001") &&
          item.r_numero === presupuesto.r_numero &&
          Number(item.elemento || 1) === Number(presupuesto.elemento || 1)
        );

        setRegistrosdet(prev => prev.filter(item => !mismoPresupuesto(item)));
        setTabladet(prev => prev.filter(item => !mismoPresupuesto(item)));
      } catch (error) {
        console.log("Error eliminando presupuesto:", error);
        alert(error.message || "No se pudo eliminar el presupuesto.");
      }
    };

    const handleTrabajoInfo = async (presupuesto, trabajo = null) => {
      if (trabajo) {
        setTrabajoInfo({ presupuesto, trabajo });
        return;
      }

      try {
        const response = await fetch(`${back_host}/ad_presupuestoserv/${periodo_trabajo || params.periodo}/${params.id_anfitrion}/${contabilidad_trabajo || params.documento_id}/${presupuesto.r_cod || "NV"}/${presupuesto.r_serie || "0001"}/${presupuesto.r_numero}/${presupuesto.elemento || 1}`);
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "No se pudieron cargar los trabajos.");
        }

        setTrabajoInfo({
          presupuesto,
          trabajos: (data.data || []).map((item) => ({
            ...item,
            id: item.servicio,
            numero: item.descripcion || `Servicio ${item.servicio || ""}`,
          })),
        });
      } catch (error) {
        console.log("Error cargando trabajos de presupuesto:", error);
        alert(error.message || "No se pudieron cargar los trabajos.");
      }
    };

    const handleOpenClonarTrabajo = (info) => {
      if (!info?.trabajo || !info?.presupuesto) {
        return;
      }

      setClonarTrabajoInfo(info);
    };

    const handleOpenClonarPresupuesto = async (presupuesto) => {
      const documentoOrigen = presupuesto.documento_id || contabilidad_trabajo || params.documento_id;
      const periodoOrigen = presupuesto.periodo || periodo_trabajo || params.periodo;

      try {
        const response = await fetch(`${back_host}/ad_presupuestoserv/${periodoOrigen}/${params.id_anfitrion}/${documentoOrigen}/${presupuesto.r_cod || "NV"}/${presupuesto.r_serie || "0001"}/${presupuesto.r_numero}/${presupuesto.elemento || 1}`);
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "No se pudieron cargar los trabajos del presupuesto.");
        }

        const trabajos = (data.data || []).map((item) => ({
          ...item,
          id: item.servicio,
          numero: item.descripcion || `Servicio ${item.servicio || ""}`,
        }));

        if (!trabajos.length) {
          alert("Este presupuesto no tiene trabajos para clonar.");
          return;
        }

        setClonarTrabajoInfo({
          presupuesto: { ...presupuesto, documento_id: documentoOrigen, periodo: periodoOrigen },
          trabajos,
          modoPresupuesto: true,
        });
      } catch (error) {
        console.log("Error cargando presupuesto para clonacion:", error);
        alert(error.message || "No se pudieron cargar los trabajos del presupuesto.");
      }
    };

    const handleCloseClonarTrabajo = () => {
      if (clonandoTrabajo) {
        return;
      }

      setClonarTrabajoInfo(null);
    };

    const handleConfirmClonarTrabajo = async (destino) => {
      const presupuestoOrigen = clonarTrabajoInfo?.presupuesto;
      const trabajoOrigen = clonarTrabajoInfo?.trabajo;
      const clonarPresupuestoCompleto = Boolean(clonarTrabajoInfo?.modoPresupuesto);
      const trabajosOrigen = clonarPresupuestoCompleto ? (clonarTrabajoInfo?.trabajos || []) : [trabajoOrigen];

      if (!presupuestoOrigen || !trabajosOrigen.length || (!clonarPresupuestoCompleto && !trabajoOrigen)) {
        return;
      }

      if (!destino?.documento_id || !destino?.periodo || (!clonarPresupuestoCompleto && !destino?.r_numero)) {
        alert(clonarPresupuestoCompleto ? "Indica la empresa y el periodo de destino." : "Indica empresa, periodo y numero de presupuesto destino.");
        return;
      }

      setClonandoTrabajo(true);
      let trabajosClonados = 0;
      let presupuestoDestinoCreado = null;

      try {
        const clonarUrl = `${back_host}/ad_presupuestoserv/clonar`;
        let presupuestoDestino = destino;

        if (clonarPresupuestoCompleto) {
          const fechaDestino = `${destino.periodo}-01`;
          const crearResponse = await fetch(`${back_host}/ad_presupuesto`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id_anfitrion: params.id_anfitrion,
              documento_id: destino.documento_id,
              periodo: destino.periodo,
              id_invitado: params.id_invitado,
              fecha: fechaDestino,
            }),
          });
          const crearData = await crearResponse.json();

          if (!crearResponse.ok || !crearData.success) {
            throw new Error(crearData.message || "No se pudo crear el presupuesto destino.");
          }

          const numeroDestino = extraerNumeroPresupuestoCreado(crearData);
          if (!numeroDestino) {
            throw new Error("El presupuesto destino se creo, pero no se recibio su numero.");
          }

          presupuestoDestino = {
            documento_id: destino.documento_id,
            periodo: destino.periodo,
            r_cod: "NV",
            r_serie: "0001",
            r_numero: numeroDestino,
            elemento: 1,
          };
          presupuestoDestinoCreado = presupuestoDestino;

          const actualizarResponse = await fetch(`${back_host}/ad_presupuesto`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              periodo: destino.periodo,
              id_anfitrion: params.id_anfitrion,
              documento_id: destino.documento_id,
              ...presupuestoDestino,
              r_fecemi: fechaDestino,
              r_fecvcto: fechaDestino,
              r_id_doc: presupuestoOrigen.r_id_doc || "6",
              r_documento_id: presupuestoOrigen.r_documento_id,
              r_razon_social: presupuestoOrigen.r_razon_social,
              r_direccion: presupuestoOrigen.r_direccion,
              contacto_nombre: presupuestoOrigen.contacto_nombre,
              contacto_celular: presupuestoOrigen.contacto_celular,
              glosa: presupuestoOrigen.glosa,
              r_moneda: presupuestoOrigen.r_moneda || "PEN",
              r_tc: Number(presupuestoOrigen.r_tc || 1),
              r_forma_pago_id: presupuestoOrigen.r_forma_pago_id,
              estado: "P",
              ctrl_mod_us: params.id_invitado,
            }),
          });
          const actualizarData = await actualizarResponse.json();

          if (!actualizarResponse.ok || !actualizarData.success) {
            throw new Error(actualizarData.message || `Se creo el presupuesto ${numeroDestino}, pero no se copiaron sus datos generales.`);
          }
        }

        for (const trabajo of trabajosOrigen) {
          const payload = {
            id_anfitrion: params.id_anfitrion,
            id_invitado: params.id_invitado,
            origen: {
              documento_id: presupuestoOrigen.documento_id || contabilidad_trabajo || params.documento_id,
              periodo: presupuestoOrigen.periodo || periodo_trabajo || params.periodo,
              r_cod: presupuestoOrigen.r_cod || "NV",
              r_serie: presupuestoOrigen.r_serie || "0001",
              r_numero: presupuestoOrigen.r_numero,
              elemento: presupuestoOrigen.elemento || 1,
              servicio: trabajo.servicio,
            },
            destino: {
              documento_id: presupuestoDestino.documento_id,
              periodo: presupuestoDestino.periodo,
              r_cod: presupuestoDestino.r_cod || "NV",
              r_serie: presupuestoDestino.r_serie || "0001",
              r_numero: presupuestoDestino.r_numero,
              elemento: presupuestoDestino.elemento || 1,
            },
          };
          const response = await fetch(clonarUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const responseText = await response.text();
          let data = {};

          try {
            data = responseText ? JSON.parse(responseText) : {};
          } catch (parseError) {
            console.log("Respuesta no JSON al clonar trabajo:", { url: clonarUrl, status: response.status, body: responseText, parseError });
            throw new Error(`El backend no devolvio JSON para ${clonarUrl}.`);
          }

          if (!response.ok || !data.success) {
            throw new Error(data.message || `No se pudo clonar el trabajo ${trabajo.servicio}.`);
          }
          trabajosClonados += 1;
        }

        if (clonarPresupuestoCompleto) {
          alert(`Presupuesto clonado correctamente: ${numeroPresupuesto(presupuestoDestino)} (${trabajosClonados} trabajos).`);
          navigate(`/ad_ventapresupuesto/${params.id_anfitrion}/${params.id_invitado}/${presupuestoDestino.periodo}/${presupuestoDestino.documento_id}/${presupuestoDestino.r_numero}/edit`);
        } else {
          alert("Trabajo clonado correctamente.");
        }
        setClonarTrabajoInfo(null);
        setTrabajoInfo(null);
        setUpdateTrigger({});
      } catch (error) {
        console.log("Error clonando trabajo:", error);
        const progreso = clonarPresupuestoCompleto && presupuestoDestinoCreado
          ? ` Se creo ${numeroPresupuesto(presupuestoDestinoCreado)} y se copiaron ${trabajosClonados} de ${trabajosOrigen.length} trabajos; revisa ese presupuesto antes de volver a intentarlo.`
          : "";
        if (clonarPresupuestoCompleto && presupuestoDestinoCreado) {
          setClonarTrabajoInfo(null);
        }
        alert(`${error.message || "No se pudo clonar."}${progreso}`);
      } finally {
        setClonandoTrabajo(false);
      }
    };
  
    const handleChange = e => {
      //Para todos los demas casos ;)
      if (e.target.name==="periodo"){
        console.log('cambiando en periodo');
        setPeriodoTrabajo(e.target.value);
        //En cada cambio, actualizar ultimo periodo seleccionado 
        sessionStorage.setItem('periodo_trabajo', e.target.value);
        //console.log('handleChange periodo_trabajo', e.target.value);
  
        setDiaSel("*"); //todo cambio de mes, evita dias incorrectos en meses 30,31,28,29
      }
      if (e.target.name==="contabilidad"){
        console.log('cambiando en contabilidad');
        setContabilidadTrabajo(e.target.value);
        //En cada cambio, actualizar ultima contabilidad seleccionada
        sessionStorage.setItem('contabilidad_trabajo', e.target.value);
        
        //filtramos su nombre para historial
        const opcionSeleccionada = contabilidad_select.find(opcion => opcion.documento_id === e.target.value).razon_social;
        sessionStorage.setItem('contabilidad_nombre', opcionSeleccionada);
        
        //debemos cambiar navigate, pra actualizar el dom
        fetchTotalVentas();
        setUpdateTrigger(Math.random());//experimento para actualizar el dom
        navigate(`/ad_ventapresupuesto/${params.id_anfitrion}/${params.id_invitado}/${periodo_trabajo}/${e.target.value}`);
      }
      if (e.target.name==="fecha_clon"){
        setFechaClon(e.target.value);
      }
      
      setUpdateTrigger(Math.random());//experimento para actualizar el dom
    }

    useEffect(() => {
      if (!periodo_trabajo || !contabilidad_trabajo) {
        return;
      }

      cargaRegistro("presupuestos", periodo_trabajo, contabilidad_trabajo, diaSel);
    }, [periodo_trabajo, contabilidad_trabajo, diaSel, updateTrigger]);
        
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        backgroundColor: "transparent",
        p: { xs: 1, md: 1.5 },
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>


        <TrHeader
          titulo="Control de Presupuestos"
          contador={data.length}
          contadorTexto={`de ${registrosdet.length} presupuestos`}
          nuevoTexto="Nuevo presupuesto"
          buscarTexto="Buscar número, cliente, documento..."
          valorBusqueda={valorBusqueda}
          onBuscar={actualizaValorFiltro}
          onNuevo={handleNuevoPresupuesto}
          compactControles
          headerExtra={(
            <Tooltip title="Exportar Excel" arrow>
              <Box>
                <AppButton
                  icon={<FileSpreadsheet size={17} />}
                  onClick={exportarPresupuestosExcel}
                  disabled={!data.length}
                  sx={{ width: 40, height: 40, minWidth: 40, p: 0, color: palette.success }}
                />
              </Box>
            </Tooltip>
          )}
        />
        <TrFiltros
          periodoTrabajo={periodo_trabajo || params.periodo}
          periodoSelect={periodo_select}
          contabilidadTrabajo={contabilidad_trabajo || params.documento_id}
          contabilidadSelect={contabilidad_select}
          puntosVentaAsignados={[]}
          onPeriodoSelect={value => handleChange({ target: { name: "periodo", value } })}
          onContabilidadSelect={value => handleChange({ target: { name: "contabilidad", value } })}
          compact
        />

        <DaySelector key={periodo_trabajo || params.periodo} period={periodo_trabajo || params.periodo} onDaySelect={handleDayFilter} defaultToAll />

        <Box sx={{ mt: 1.15, mb: 1.1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 0.65 }}>
          <PresupuestoFiltroCard label="Total presupuestos" value={registrosdet.length} icon={ClipboardList} color={palette.accent} soft={palette.accentSoft} active={estadoFiltro === "*" && cpeFiltro === "*"} onClick={() => { setEstadoFiltro("*"); setCpeFiltro("*"); }} />
          <PresupuestoEstadoFiltroCard
            pendientes={registrosdet.filter(row => (row.estado || "P") === "P").length}
            cerrados={registrosdet.filter(row => (row.estado || "P") === "C").length}
            estadoFiltro={estadoFiltro}
            onFilter={(estado) => setEstadoFiltro(actual => actual === estado ? "*" : estado)}
          />
          <PresupuestoCpeFiltroCard
            generados={registrosdet.filter(tieneComprobanteGenerado).length}
            sinCpe={registrosdet.filter(row => !tieneComprobanteGenerado(row)).length}
            cpeFiltro={cpeFiltro}
            onFilter={(filtro) => setCpeFiltro(actual => actual === filtro ? "*" : filtro)}
          />
          <PresupuestoFiltroCard label="Monto total" value={resumirMontosPresupuestos(registrosdet)} icon={FileSpreadsheet} color={palette.accent} soft={palette.accentSoft} title={resumirMontosPresupuestos(registrosdet)} />
        </Box>

        
        {/* TABLA */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowX: "hidden",
            "& .rdt_Table, & .rdt_TableWrapper, & .rdt_TableBody": {
              width: "100% !important",
              maxWidth: "100% !important",
              minWidth: "0 !important",
              boxSizing: "border-box",
            },
            "& .rdt_TableRow": {
              width: "100% !important",
              maxWidth: "100% !important",
              minWidth: "0 !important",
              boxSizing: "border-box",
              "@media (max-width: 600px)": {
                width: "90% !important",
                maxWidth: "90% !important",
                marginLeft: "0 !important",
                marginRight: "auto !important",
              },
            },
            "& .rdt_TableCell": {
              minWidth: "0 !important",
              maxWidth: "100% !important",
              boxSizing: "border-box",
            },
          }}
        >
        <DataTable
          theme="solarized"
          columns={createColumns(
            handleEditPresupuesto,
            handleViewPresupuesto,
            handlePdfPresupuesto,
            handleTrabajoInfo,
            handleDeletePresupuesto,
            handleGenerarComprobantePresupuesto,
            renderSunatComprobantePresupuesto,
            handleOpenComprobanteGenerado,
            handleOpenClonarPresupuesto
          )}
          data={data}
          pagination
          paginationPerPage={10}
          paginationResetDefaultPage={paginaReset}
          noDataComponent={<Box sx={{ p: 4, color: palette.muted }}>Sin presupuestos para los filtros seleccionados</Box>}
          highlightOnHover
          responsive
          customStyles={customStyles}
          conditionalRowStyles={[
            {
              when: row => (row.estado || "P") === "P",
              style: {
                border: `1px solid ${palette.warning}`,
                borderLeft: `4px solid ${palette.warning}`,
              },
            },
          ]}
        />
        </Box>

        <TrabajoInfoModal
          trabajoInfo={trabajoInfo}
          onClose={() => setTrabajoInfo(null)}
          onSelectTrabajo={(trabajo) => setTrabajoInfo(prev => ({ ...prev, trabajo }))}
          onCloneTrabajo={handleOpenClonarTrabajo}
        />

        <ClonarTrabajoModal
          open={Boolean(clonarTrabajoInfo)}
          trabajoInfo={clonarTrabajoInfo}
          periodoOptions={periodo_select}
          contabilidadOptions={contabilidad_select}
          defaultPeriodo={periodo_trabajo || params.periodo}
          defaultDocumentoId={contabilidad_trabajo || params.documento_id}
          onClose={handleCloseClonarTrabajo}
          onConfirm={handleConfirmClonarTrabajo}
          loading={clonandoTrabajo}
          modoPresupuesto={Boolean(clonarTrabajoInfo?.modoPresupuesto)}
        />

      </Box>

    </Box>
  );
}
